import React, { useCallback, useMemo } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useGetPartnerOffering } from "../../data/api";
import {
  BookingType,
  OrderableType,
  useLocationV2Query,
  useSpaceV2Query,
} from "../../offering/api";
import { PartnerConfig } from "../../offering/partner_config";
import OfferingDetailsBase from "./template_offering_details_base";
import { useHandlePressBack } from "../hooks/use_handle_press_back";
import { EventBookingContainer } from "../components/event_and_layout/event_booking_container";
import {
  DateTimeValue,
  isHourlyValue,
} from "../components/date-time-picker/date_time_picker";
import {
  useDateTimePickerHelper,
  toQuoteInput,
  useEventSpaceOrderParamsHandlers,
  normalizeEventSpaceOrderParams,
} from "@/offering/order_params";
import { buildPathWithParams, useMediaQuery, useQueryString } from "@/hooks";
import {
  useCurrentQueryString,
  useSyncQueryStringV2,
} from "@/offering/offering_helpers";
import { parseISO } from "date-fns";
import { useQuoteQuery } from "@/offering/hooks/use_quote_query";
import { CheckoutBoxMobile, UnitPrice } from "../components";
import { PriceLabelDesktop } from "../components/price_label";
import { useI18n } from "@/lib/i18n/i18n_provider";
import { calculateDailyBenefitPercent } from "../utils/calculate_daily_benefit_percent";
import { getMaxHoursFromBookingHours } from "../utils/get_max_hours_from_booking_hours";
import { Button } from "react-bootstrap";
import { QuoteDetails } from "../components/quote_view";
import { formatCurrency } from "@/lib/currency/currency";
import { Divider } from "@/offering/divider";
import { PricingType } from "@/data";
import { Spacer } from "@/offering/spacer";

export interface TemplateOfferingDetailsEventParams {
  offeringID: string;
  spaceID: string;
}

interface EventSpaceQueryParams {
  startDate?: string;
  endDate?: string;
  date?: string;
  startTime?: string;
  endTime?: string;
  attendees?: string;
  eventTypeID?: string;
  eventTypeDescription?: string;
  layoutID?: string;
}

interface TemplateOfferingDetailsEventProps {
  partnerConfig: PartnerConfig;
}

export function TemplateOfferingDetailsEvent({
  partnerConfig,
}: TemplateOfferingDetailsEventProps) {
  const { device } = useMediaQuery();
  const { fbt } = useI18n();
  const pageParams = useParams<TemplateOfferingDetailsEventParams>();
  const queryParams = useQueryString<EventSpaceQueryParams>();
  const history = useHistory();

  const partnerOfferingID = pageParams.offeringID;
  const spaceID = pageParams.spaceID;

  const partnerOffering = useGetPartnerOffering(partnerOfferingID);

  const space = useSpaceV2Query(spaceID);

  const location = useLocationV2Query(space.locationID);

  const { handlePressBackFromDetailsPage } = useHandlePressBack();

  const {
    orderParams,
    handleChangeDateRange,
    handleChangeAttendees,
    handleChangeEventType,
    handleChangeLayout,
  } = useEventSpaceOrderParamsHandlers(
    normalizeEventSpaceOrderParams(queryParams)
  );
  useSyncQueryStringV2(orderParams);

  const {
    handleSelectDate,
    handleNextVisibleDates,
    timeslots,
    availableDays,
    disableRangeSelection,
  } = useDateTimePickerHelper(spaceID, {
    dateRange: {
      startDate: isHourlyValue(orderParams.dateRange)
        ? orderParams.dateRange?.date
        : orderParams.dateRange?.startDate,
      endDate: isHourlyValue(orderParams.dateRange)
        ? orderParams.dateRange?.date
        : orderParams.dateRange?.endDate,
    },
  });

  const quoteInput = useMemo(() => {
    return spaceID && orderParams.layoutID && orderParams.attendees
      ? toQuoteInput(
          spaceID,
          partnerOffering.partner.id,
          orderParams,
          "WL",
          {
            layoutID: orderParams.layoutID,
            attendees: orderParams.attendees,
          },
          undefined,
          availableDays
        )
      : null;
  }, [spaceID, partnerOffering.partner.id, orderParams, availableDays]);

  const { data: quote } = useQuoteQuery(quoteInput);

  const defaultQuoteOrderItem = quote?.orderItems[0];
  const unitPrice = defaultQuoteOrderItem?.unitPrice;
  const bookingType =
    defaultQuoteOrderItem?.type === OrderableType.Booking
      ? defaultQuoteOrderItem.details.type
      : undefined;

  const maxHours = getMaxHoursFromBookingHours(space.bookingHours);
  const savingPercent = calculateDailyBenefitPercent(
    space.pricings.find((p) => p.type === PricingType.Hourly)?.price,
    space.priceRanges.daily?.lowestPrice,
    maxHours?.openAt,
    maxHours?.closedAt
  );

  const handleChangeDateTime = (value?: DateTimeValue) => {
    const onlyDateSelected =
      isHourlyValue(value) && value.date && !value.startTime && !value.endTime;
    if (onlyDateSelected && value.date) {
      handleSelectDate(parseISO(value.date));
    } else {
      handleChangeDateRange(value);
    }
  };

  const queryString = useCurrentQueryString(orderParams);

  const handleContinue = useCallback(() => {
    const pathWithParams = buildPathWithParams(
      `/offerings/${partnerOfferingID}/spaces/${spaceID}/checkout?${queryString}`
    );
    history.push(pathWithParams);
  }, [history, partnerOfferingID, spaceID, queryString]);

  const eventBookingContainer = (
    <div className={device !== "mobile" ? "border rounded p-4" : undefined}>
      {!!(unitPrice && bookingType) ? (
        <UnitPrice
          price={unitPrice}
          currency={space.currencyCode}
          priceUnit={fbt(bookingType === BookingType.Hourly ? "/hour" : "/day")}
        />
      ) : (
        <PriceLabelDesktop
          priceRanges={space.priceRanges}
          currency={space.currencyCode}
          hourlyPrice={space.priceRanges.hourly?.lowestPrice}
          dailySavingPercent={savingPercent}
        />
      )}
      <Spacer size={32} />
      <div data-testid="event-space-booking-container">
        <EventBookingContainer
          availableDays={availableDays}
          timeslots={timeslots}
          space={space}
          value={orderParams}
          disableRangeSelection={disableRangeSelection}
          onChangeDateTime={handleChangeDateTime}
          onChangeMonth={handleNextVisibleDates}
          onChangeEventType={handleChangeEventType}
          onChangeLayout={handleChangeLayout}
          onChangeNumberOfGuests={handleChangeAttendees}
          disableUpdateFinalStep
        />
        <Button disabled={!quote} className="w-100" onClick={handleContinue}>
          Continue
        </Button>
        {quote && (
          <div className="mt-4">
            <QuoteDetails quote={quote} currency={space.currencyCode} />
            <div className="my-3">
              <Divider />
            </div>
            <div className="d-flex justify-content-between px-3">
              <b>Total</b>
              <b>{formatCurrency(quote.totalPrice, space.currencyCode)}</b>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <OfferingDetailsBase
      partnerOffering={partnerOffering}
      partnerOfferingID={partnerOfferingID}
      partnerConfig={partnerConfig}
      onPressBack={() => handlePressBackFromDetailsPage(partnerOfferingID)}
      selectedSpace={space}
      orderParams={orderParams}
      data={{
        imagesMobile: space.images
          .map((i) => i.small)
          .concat(location.images.map((i) => i.small)),
        imagesDesktop: space.images
          .map((i) => i.medium)
          .concat(location.images.map((i) => i.medium)),
        imagesModal: space.images
          .map((i) => i.large)
          .concat(location.images.map((i) => i.large)),
        location: location,
        description: space.description && space.description.length > 0 && (
          <div dangerouslySetInnerHTML={{ __html: space.description }} />
        ),
        pageTitle: space.name,
        title: space.name || partnerOffering.offeringTitle,
        offeringName: `${space.name}, ${location.name}`,
        layouts: space.layouts,
      }}
      checkoutBoxDesktop={eventBookingContainer}
      checkoutBoxMobile={
        <CheckoutBoxMobile
          checkoutBox={eventBookingContainer}
          quote={quote}
          space={space}
        />
      }
    />
  );
}
